import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/auth-store'

/**
 * SSO callback page. The backend redirects the browser here after a successful
 * Okta OIDC flow with tokens packed into the URL fragment so they never reach
 * the server. We parse, store, fetch the user, and redirect to the launchpad.
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const completeSso = async () => {
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash
      const params = new URLSearchParams(hash)
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      const expiresAtRaw = params.get('expires_at')
      const returnTo = params.get('return_to') || '/'

      if (!accessToken || !refreshToken || !expiresAtRaw) {
        setError('Missing SSO tokens in callback URL.')
        return
      }

      const expiresAt = Number(expiresAtRaw)
      if (!Number.isFinite(expiresAt)) {
        setError('Invalid token expiration timestamp.')
        return
      }

      // Set tokens first so the /auth/me request has Authorization.
      useAuthStore.setState({
        tokens: { accessToken, refreshToken, expiresAt },
        isAuthenticated: true,
        lastActivity: Date.now(),
      })

      try {
        const user = await authApi.getCurrentUser()
        if (cancelled) return
        login(user, { accessToken, refreshToken, expiresAt })
        // Strip the hash before navigating so tokens don't linger in browser history.
        window.history.replaceState(null, '', window.location.pathname)
        navigate(returnTo, { replace: true })
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load user profile.'
        setError(message)
      }
    }

    void completeSso()
    return () => {
      cancelled = true
    }
  }, [login, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full p-8 space-y-6 text-center">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-muted">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">Sign-in failed</h1>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button onClick={() => navigate('/login', { replace: true })} className="w-full">
            Return to login
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full p-8 space-y-4 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
        <p className="text-muted-foreground">Completing sign-in…</p>
      </div>
    </div>
  )
}

export default OAuthCallbackPage
